import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import logo from "../res/logo.png";

function FeaturePage({ data, features }) {
  const others = useMemo(
    () =>
      features.filter(
        (f) =>
          f.id &&
          f.url &&
          f.id !== data.id &&
          f.url !== data.url
      ),
    [features, data.id, data.url]
  );

  const paragraphs = useMemo(() => {
    const raw = data.body != null ? String(data.body) : "";
    return raw.split("\n").filter((line) => line.trim().length > 0);
  }, [data.body]);

  useEffect(() => {
    document.title = data.title ? `${data.title} | PEACI` : "Feature | PEACI";
  }, [data.title]);

  return (
    <main
      className="w-full bg-stone-50 dark:bg-stone-900/30 py-8 sm:py-12"
      role="main"
    >
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
        <nav
          className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-stone-600 dark:text-stone-400"
          aria-label="Breadcrumb"
        >
          <Link
            to="/"
            className="font-medium text-sky-700 transition-colors hover:text-sky-600 hover:underline dark:text-sky-400 dark:hover:text-sky-300"
          >
            Home
          </Link>
          <span className="text-stone-400" aria-hidden>
            /
          </span>
          <Link
            to="/#home-features-heading"
            className="font-medium text-sky-700 transition-colors hover:text-sky-600 hover:underline dark:text-sky-400 dark:hover:text-sky-300"
          >
            Features
          </Link>
          <span className="text-stone-400" aria-hidden>
            /
          </span>
          <span className="line-clamp-1 text-stone-500 dark:text-stone-500">
            {data.title}
          </span>
        </nav>

        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
          <article className="min-w-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none">
            <figure className="m-0">
              <div className="aspect-[21/9] w-full overflow-hidden bg-stone-100 sm:aspect-[2/1]">
                <img
                  src={data.imgurl || logo}
                  alt={data.title ? `Illustration for: ${data.title}` : "Feature image"}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = logo;
                  }}
                />
              </div>
              {data.date ? (
                <figcaption className="border-b border-stone-100 px-6 pt-3 pb-2 text-sm font-medium text-sky-600 dark:border-gray-700 dark:text-sky-400 sm:px-8">
                  {data.date}
                </figcaption>
              ) : null}
            </figure>

            <div className="p-6 sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-sky-600 dark:text-sky-400">
                Feature
              </p>
              <h1 className="mt-2 text-2xl font-bold tracking-tight text-stone-900 dark:text-white sm:text-3xl lg:text-4xl">
                {data.title}
              </h1>

              <div className="prose prose-stone mt-6 max-w-none dark:prose-invert">
                {paragraphs.length > 0 ? (
                  paragraphs.map((p, key) => (
                    <p
                      className="mb-3 text-base leading-relaxed text-stone-700 last:mb-0 dark:text-stone-300"
                      key={key}
                    >
                      {p}
                    </p>
                  ))
                ) : (
                  <p className="text-base text-stone-500 dark:text-stone-400">
                    No article text available.
                  </p>
                )}
              </div>
            </div>
          </article>

          <aside className="w-full shrink-0 lg:w-72 xl:w-80">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:shadow-none lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              <header className="mb-4 shrink-0">
                <h2 className="text-lg font-bold text-stone-900 dark:text-white sm:text-xl">
                  More features
                </h2>
                <div
                  className="mt-1 h-0.5 w-16 rounded-full bg-sky-500"
                  aria-hidden
                />
              </header>

              {others.length === 0 ? (
                <p className="text-sm text-stone-500 dark:text-stone-400">
                  No other features to show.
                </p>
              ) : (
                <ul className="flex flex-col gap-2.5">
                  {others.map((val) => (
                    <li key={val.id || val.url}>
                      <Link
                        to={`/${val.url}`}
                        className="group flex gap-3 overflow-hidden rounded-lg border border-transparent p-1.5 transition-colors hover:border-sky-200 hover:bg-stone-50 dark:hover:border-sky-900/50 dark:hover:bg-gray-700/50"
                      >
                        <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-md bg-stone-100 ring-1 ring-stone-200/80 dark:ring-gray-600">
                          <img
                            src={val.imgurl || logo}
                            alt=""
                            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
                            onError={(e) => {
                              e.currentTarget.src = logo;
                            }}
                          />
                        </div>
                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 py-0.5">
                          <span className="line-clamp-2 text-sm font-semibold leading-snug text-stone-900 group-hover:text-sky-600 dark:text-white dark:group-hover:text-sky-400">
                            {val.title}
                          </span>
                          {val.date ? (
                            <span className="text-xs text-sky-600 dark:text-sky-400">
                              {val.date}
                            </span>
                          ) : null}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default FeaturePage;
